import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { authEndPoints, onboardingEndPoints } from "../urlConfig/apiEndpoints";
import type { ApiResponse } from "./ApiResponse";
import type {
  IndustryResponseWrapper,
  LoginResponse,
  SubSectorsResponse,
} from "@/app/types/auth.types";
import {
  showError,
  showSuccess,
  extractErrorMessage,
} from "../errorservices/errorservices";

import { executeLogout } from "@/app/utils/loginFlow/logoutHelper";
import { store, type RootState } from "@/app/redux/store";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/app/utils/loginFlow/correlationId";

type MethodType = "get" | "post" | "put" | "delete";

interface ApiRequestConfig {
  method: MethodType;
  url: string;
  payload?: Record<string, any>;
  suppressToast?: boolean;
  requiresToken?: boolean;
  token?: string;
}

// ........................growxteamview.......................................
interface GxTermsPayload {
  gxLookupType: "TERMS_CONDITION" | string;
}

interface GxLookupItem {
  gxLookupId: number;
  gxLookupType: string;
  gxLookupCode: string;
  gxLookupValue: string;
  gxLookupDesc: string | null;
  gxLookupSeq: number;
  menuId: number;
  permissionId: number;
}

interface GxApiEnvelope<T> {
  data: T;
  status: number;
  errorCode: string;
  errorMessage: string;
  meta: unknown;
}

type GxTermsResponse = GxApiEnvelope<GxLookupItem[]>;

const getTenantId = (): string | undefined => {
  const state = store.getState() as RootState;
  return state.userData?.data?.tenantId;
};

// ---------------- Auth logout registration ----------------
const handleUnauthorized = (err: any) => {
  executeLogout();
  return Promise.reject(err);
};

const handleApiRequest = async <T>({
  method,
  url,
  payload = {},
  suppressToast = false,
  requiresToken = true,
  token = "",
}: ApiRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const correlationId = generateCorrelationId();
    const tenantId = getTenantId();
    const headers: Record<string, string | undefined> = {
      Accept: "*/*",
      appId: tenantId,
      [CORRELATION_ID_HEADER]: correlationId,
      "Content-Type":
        payload instanceof FormData ? undefined : "application/json",
    };

    if (requiresToken && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const config: AxiosRequestConfig = { headers };

    let response: AxiosResponse<ApiResponse<T>>;

    if (method === "get" || method === "delete") {
      response = await axios[method](url, { ...config, params: payload });
    } else {
      response = await axios[method](url, payload, config);
    }

    const responseData = response.data;

    if (responseData.status === 1) {
      if (!suppressToast) {
        const msg = responseData.message || responseData.successMessage;
        if (msg) showSuccess(msg);
      }
      return responseData;
    }

    const errorMsg = responseData.errorMessage || "Something went wrong.";
    if (!suppressToast) showError(responseData.status, errorMsg);
    throw new Error(errorMsg);
  } catch (err: any) {
    const { code, message } = extractErrorMessage(err);

    if (code === 401) {
      return handleUnauthorized(err);
    }

    if (!suppressToast) showError(code, message);
    throw err;
  }
};

// ---------------- API FUNCTIONS ----------------

export const gxloginPassApi = (payload: Record<string, any>) =>
  handleApiRequest<any>({
    method: "post",
    url: onboardingEndPoints.gxloginPassword,
    payload,
    requiresToken: false,
  });

export const gxrequestOtpApi = (payload: Record<string, any>) =>
  handleApiRequest<any>({
    method: "post",
    url: onboardingEndPoints.gxrequestLoginOtp,
    payload,
    requiresToken: false,
  });

export const gxverifyLoginOtpApi = (payload: Record<string, any>) =>
  handleApiRequest<LoginResponse>({
    method: "post",
    url: onboardingEndPoints.gxverifyLoginOtp,
    payload,
    requiresToken: false,
  });
// ........................gxUserRolePutApi.......................................
export const gxLoginTokenApi = (payload: Record<string, unknown>) => {
  return handleApiRequest<any>({
    method: "post",
    url: onboardingEndPoints.gxlogintoken,
    payload,
    requiresToken: false,
  });
};

// ........................gxlogout.......................................
export const gxLogouTokenApi = (payload: Record<string, unknown>) => {
  return handleApiRequest<any>({
    method: "post",
    url: onboardingEndPoints.gxlogout,
    payload,
    requiresToken: false,
  });
};

// ........................gxfeecreateApi.......................................

export const gxfeecreateApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxfeecreate,
    payload,
    requiresToken: false,
  });

// ........................gxfeeuploadApi.......................................

export const gxfeeuploadApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxfeeupload,
    payload,
    requiresToken: false,
  });

// ........................gxfeeviewApi.......................................
export const gxfeeviewApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxfeeview,
    payload,
    requiresToken: false,
  });

// ........................gxvalidateApi.......................................
export const gxvalidateApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxvalidate,
    payload,
    requiresToken: false,
  });
// ........................growxcontactvalidate.......................................
export const gxcontactvalidateApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxcontactvalidate,
    payload,
    requiresToken: false,
  });

// ........................gxglobalcountryApi.......................................
export const gxglobalcountryApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxglobalcountry,
    payload,
    requiresToken: false,
  });

// ........................gxgrowxsupportcountryApi.......................................
export const gxgrowxsupportcountryApi = () =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxsupportcountry,
    requiresToken: false,
  });

// ........................gxgrowxcompanycheckApi.......................................
export const gxgrowxcompanycheckApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxcompanycheck,
    requiresToken: false,
    payload,
  });

// ........................gxgrowxstatusupdateApi.......................................
export const gxgrowxstatusupdateApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxstatusupdate,
    requiresToken: false,
    payload,
  });

// ........................gxgrowxregisterApi.......................................
export const gxgrowxregisterApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxregister,
    requiresToken: false,
    payload,
  });

// ........................growxfeeviewtab.......................................
export const gxgrowxfeeviewtabApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxfeeviewtab,
    requiresToken: false,
    payload,
  });

// ........................growxteamview.......................................

export const gxgrowxtermsApi = (payload: GxTermsPayload) =>
  handleApiRequest<GxTermsResponse>({
    method: "post",
    url: authEndPoints.growxterms,
    requiresToken: false,
    payload,
  });

// .......................menu............................

export const growxverify = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxverifykfc,
    requiresToken: false,
    payload,
  });

// ........................gxfeeviewApi.......................................
export const gxlookupApi = (payload: any) =>
  handleApiRequest<any>({
    method: "post",
    url: authEndPoints.growxterms,
    payload,
    requiresToken: false,
  });

  
export const gxindustrieslistApi = () =>
  handleApiRequest<IndustryResponseWrapper>({
    method: "post",
    url: authEndPoints.gxindustrieslist,
    requiresToken: false,
  });

export const gxsubsectorslistApi = (payload: { type: string }) =>
  handleApiRequest<SubSectorsResponse>({
    method: "post",
    url: authEndPoints.gxsubsectorslist,
    requiresToken: false,
    payload,
  });
